import { useState, useEffect, useRef, useCallback } from 'react';

// Detect when element enters viewport
export function useOnScreen(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// Scroll position
export function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const h = () => setY(window.scrollY);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);
  return y;
}

// Counter animation
export function useCounter(end, duration = 2000) {
  const [count, setCount] = useState(0);
  const [ref, visible] = useOnScreen(0.3);
  useEffect(() => {
    if (!visible) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setCount(Math.floor(p * end));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [visible, end, duration]);
  return [ref, count];
}

// Image slider
export function useSlider(length, interval = 4000) {
  const [index, setIndex] = useState(0);
  const next = useCallback(() => setIndex(i => (i + 1) % length), [length]);
  const prev = useCallback(() => setIndex(i => (i - 1 + length) % length), [length]);
  const goTo = useCallback((i) => setIndex(i), []);
  useEffect(() => {
    const t = setInterval(next, interval);
    return () => clearInterval(t);
  }, [next, interval]);
  return { index, next, prev, goTo };
}
